const reloadStorageKey = 'forma-initial-app-preload-reload';

export interface InitialAppRecoveryEnvironment {
  currentEntry: string | null;
  fetchEntry(): Promise<string | null>;
  storage: Pick<Storage, 'getItem' | 'setItem'>;
  reload(): void;
}

function browserEnvironment(): InitialAppRecoveryEnvironment {
  return {
    currentEntry:
      document
        .querySelector<HTMLScriptElement>('script[type="module"][src]')
        ?.getAttribute('src') ?? null,
    storage: window.sessionStorage,
    reload: () => window.location.reload(),
    fetchEntry: async () => {
      const response = await fetch('/app-shell.html', {
        cache: 'no-store',
        signal: AbortSignal.timeout(5000),
      });
      if (!response.ok) return null;
      const document = new DOMParser().parseFromString(
        await response.text(),
        'text/html',
      );
      return (
        document
          .querySelector<HTMLScriptElement>('script[type="module"][src]')
          ?.getAttribute('src') ?? null
      );
    },
  };
}

/** Only wrap the initial app import: reloading a mounted app can lose edits. */
export async function loadInitialApp<T>(
  importer: () => Promise<T>,
  environment?: InitialAppRecoveryEnvironment,
): Promise<T> {
  try {
    return await importer();
  } catch (cause) {
    if (
      cause instanceof Error &&
      /^Unable to preload CSS for \/assets\/[^\s]+\.css$/.test(cause.message)
    ) {
      try {
        const recovery = environment ?? browserEnvironment();
        if (
          recovery.currentEntry &&
          !recovery.storage.getItem(reloadStorageKey)
        ) {
          const hostedEntry = await recovery.fetchEntry();
          if (hostedEntry && hostedEntry !== recovery.currentEntry) {
            // Persist before reloading. Unavailable storage must never allow a loop.
            recovery.storage.setItem(reloadStorageKey, 'attempted');
            if (recovery.storage.getItem(reloadStorageKey) === 'attempted') {
              recovery.reload();
            }
          }
        }
      } catch {
        // Keep the original preload failure when recovery itself is unavailable.
      }
    }
    throw cause;
  }
}
